import {dlv} from './dlv.js';
import {getPreviewURL} from './get-preview-url.js';
import {truncate} from "./truncate.js";
import {setAlert} from "./alert.js";
import {initLightboxActions} from "./lightbox.js";

const keyMapping = {
    docNum: 'Dokumentnummer',
    archive: 'Sammlung',
    creator: 'FotografIn',
    year: 'Datierung',
    place: 'Adresse',
    accuracy: 'Genauigkeit',
};

export function createMarkerEntries(entries, elements) {
    if(!entries) {
        console.error("Error: Can't create detail view if marker json or html element is missing!");
        return;
    }

    setViewActive('list', 'add', elements.column);

    entries.forEach(entry => {
        const title = dlv(entry, 'title', 'Unbekannt');
        const docNum = dlv(entry, 'docNumber');
        const archive = dlv(entry, 'archive');
        const attachment = dlv(entry, 'attachment');
        const image = getPreviewURL(attachment);

        const clampedTitle = truncate(title, 50, '...');

        let subHeading = docNum;
        if (archive != null) subHeading = subHeading + ' - ' + archive;
        subHeading = truncate(subHeading, 60, '...');

        const entryEl = document.createElement('li');
        entryEl.className = 'location-search_aside-list-item';
        entryEl.innerHTML = `
            <button class="location-search_aside-list-item-btn">
                <div class="location-search_aside-list-item-image-container">
                  <div class="css-124emkn" style="background-image: url(${image});">
                    <img src="${image}" alt="${title} - Medien ID: ${attachment}" class="visibility-hidden">
                    </div>
                </div>
            <div>
              <p class="location-search_aside-list-item-headline mb-2">${clampedTitle}</p>
              <p class="location-search_aside-list-item-meta">${subHeading}</p>
            </div>
          </button>
        `;

        entryEl.querySelector('.location-search_aside-list-item-btn').addEventListener('click', (e) => {
            e.preventDefault();
            createMarkerEntry(entry, elements);
        });
        elements.list.appendChild(entryEl);
    });
}

export function createMarkerEntry(entry, elements) {
    if(!entry) {
        console.error("Error: Can't create detail view if marker json or html element is missing!");
        return;
    }

    setViewActive('detail', 'add', elements.column);

    const docNum = dlv(entry, 'docNumber');
    const title = dlv(entry, 'title', 'Unbekannt');
    const creator = dlv(entry, 'photographer');
    const archive = dlv(entry, 'archive');
    const place = dlv(entry, 'place');
    const year = dlv(entry, 'year');
    const attachment = dlv(entry, 'attachment');
    const image = getPreviewURL(attachment);
    const detailUrl = `/dokument/${docNum}/${attachment}`;
    let accuracy = dlv(entry, 'accuracy');

    // Map accuracy enum to display values
    if (accuracy != null) {
        switch (accuracy) {
            case 'ADDRESS':
                accuracy = 'Exakte Adresse erkannt';
                break;
            case 'STREET':
                accuracy = 'Straße bekannt, Hausnummer unbekannt';
                break;
            case 'STREET_OR_REGION':
                accuracy = 'Nur Stadt oder Land bekannt';
                break;
            case 'APPROXIMATE':
            default:
                accuracy = 'Nur Stadt oder Land bekannt';
                break;
        }
    }

    const entryEl = document.createElement('div');
    entryEl.className = 'marker-detail-view';
    createEntryImage({title, docNum, attachment, image, archive, creator}, entryEl);
    createEntryHead({title, creator, year}, entryEl);
    createEntryInfos({docNum, archive, creator, year, place, accuracy}, entryEl);
    createEntryActions({docNum, detailUrl}, entryEl);

    elements.detail.appendChild(entryEl);

    setTimeout(() => {
        initWrongLocationForm(elements.detail, docNum);
    },250);
}

function createEntryImage(data, el) {
    const imageEl = document.createElement('div');
    imageEl.className = 'marker-detail-view_image-container';
    imageEl.innerHTML = `
        <div class="marker-detail-view_image" style="background-image: url(${data.image});">
            <img src="${data.image}" alt="${data.title} - Medien ID: ${data.attachment}" class="visually-hidden">
        </div>
        
        <button type="button" data-lightbox="add" title="Auf den Leuchttisch legen" class="button button-lightbox d-none">
            <svg focusable="false" viewBox="0 0 384 512" aria-hidden="true" class="button-icon--small">
                <path d="M368 224H224V80c0-8.84-7.16-16-16-16h-32c-8.84 0-16 7.16-16 16v144H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h144v144c0 8.84 7.16 16 16 16h32c8.84 0 16-7.16 16-16V288h144c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z" style="fill: currentcolor;"></path>
            </svg>
        </button>
        <button type="button" data-lightbox="remove" title="Vom Leuchttisch entfernen" class="button button-lightbox d-none">
            <svg focusable="false" viewBox="0 0 384 512" aria-hidden="true" class="button-icon--small">
                <path d="M368 224H16c-8.84 0-16 7.16-16 16v32c0 8.84 7.16 16 16 16h352c8.84 0 16-7.16 16-16v-32c0-8.84-7.16-16-16-16z" style="fill: currentcolor;"></path>    
            </svg>
        </button>
    `;

    const addButton = imageEl.querySelector('.button[data-lightbox="add"]');
    const removeButton = imageEl.querySelector('.button[data-lightbox="remove"]');

    initLightboxActions(
        { addEl: addButton, removeEl: removeButton },
        {
            dataNumber: data.docNum,
            dataAttachment: data.attachment,
            archive: data.archive,
            creator: data.creator,
            title: data.title,
            thumbnail: data.image
        }
    );

    el.appendChild(imageEl);
}
function createEntryHead(data, el) {
    const headline = data.title ? `<h2 class="mb-2">${data.title}</h2>` : null;
    let subheadline = null;
    if(data.year || data.creator) {
        subheadline = `<p>${data.creator}`+(data.creator && data.year ? ' - ': '')+`${data.year}</p>`;
    }

    const headEl = document.createElement('div');
    headEl.className = 'marker-detail-view_head mb-3';
    headEl.innerHTML = `${headline}`;

    el.appendChild(headEl);
}
function createEntryInfos(data, el) {
    const infoEl = document.createElement('div');
    infoEl.className = 'marker-detail-view_infos mb-4';

    Object.keys(data).forEach(key => {
        const value = data[key];
        if(value) {
            infoEl.innerHTML += `
                <p class="marker-detail-view_info">
                    <strong class="marker-detail-view_info_key">${keyMapping[key]}:&nbsp;</strong>
                    <span class="marker-detail-view_info_value">${value}</span>
                </p>
            `;
        }
    });

    el.appendChild(infoEl);
}
function createEntryActions(data, el) {
    const actionEl = document.createElement('div');
    actionEl.className = 'marker-detail-view_actions mb-3';
    actionEl.innerHTML = `
        <a href="${data.detailUrl}" title="Details - ${data.docNum}" target="blank" rel="noopener noreferrer" class="marker-detail-view_action mb-2">
            <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                <path d="M19 19H5V5h7V3H5a2 2 0 00-2 2v14c0 1.1.9 2 2 2h14a2 2 0 002-2v-7h-2v7zM14 3v2h3.6l-9.8 9.8 1.4 1.4L19 6.4V10h2V3h-7z" style="fill: currentcolor;"></path>
            </svg>
            Details
        </a>
        
        <details class="w-100">
            <summary title="Falschen Ort melden" class="marker-detail-view_action">
                <svg focusable="false" viewBox="0 0 24 24" aria-hidden="true">
                    <path d="M20 2H4a2 2 0 00-2 2v18l4-4h14a2 2 0 002-2V4a2 2 0 00-2-2zm-7 12h-2v-2h2v2zm0-4h-2V6h2v4z" style="fill: currentcolor;"></path>
                </svg>
                Dieses Bild ist am falschen Ort
            </summary>
            
            <div class="marker-detail-view_action-report">
                <p>Helfen Sie uns falsche Angaben zu Adresse oder Ort zu korrigieren.</p>
                <form class="needs-validation mb-4" novalidate>
                    <div class="mb-3">
                        <label for="message" class="visually-hidden">Text</label>
                        <textarea name="message" class="form-control" id="message">&nbsp;</textarea>
                        <p>Die Angabe einer Korrektur ist optional.</p>
                    </div>
                    <div class="marker-detail-view_action-report_btn-group">
                        <button type="reset" class="button-reset">Abbrechen</button>
                        <button type="submit" class="button-send">Senden</button>
                    </div>
                </form>
            </div>
        </details>
    `;

    actionEl.querySelector('.button-reset').addEventListener('click', (e) => {
        e.preventDefault();
        actionEl.querySelector('details').removeAttribute('open');
    });

    el.appendChild(actionEl);
}
export function setViewActive(view, state, el) {
    if(state === 'add') {
        if(view === 'list') {
            el.classList.add('list-view');
            el.classList.remove('detail-view');
        } else if(view === 'detail'){
            el.classList.add('detail-view');
        }
    } else if(state === 'remove') {
        if(view === 'list') {
            el.classList.remove('list-view');
        } else if(view === 'detail'){
            el.classList.remove('detail-view');
        }
    }
}
async function setWrongLocation(apsImageId, data) {
    try {
        const response = await fetch(`/api/document/${apsImageId}/location-wrong`, {
            method: 'POST',
            credentials: 'include',
            body: JSON.stringify(data)
        });
        return await response.json();
    } catch (error) {
        //console.error("Error:", error);
        return {error: true, message: error.message};
    }
}
function initWrongLocationForm(element, docNum,attachment) {
    const form = element.querySelector('.needs-validation');
    if(!form) return;
    form.addEventListener('submit', async e => {
        e.preventDefault();
        e.stopPropagation();
        if (!form.checkValidity()) {
            console.error('Wrong location form is not valid: aborting submit');
        } else {
            const formData = new FormData(e.target);
            const data = Object.fromEntries(formData.entries());
            const formSubmitResponse = await setWrongLocation(docNum, data);

            if(formSubmitResponse.length === 0) {
                setAlert('success', 'Vielen Dank für Ihre Meldung!');
                form.reset();
                element.querySelector('.marker-detail-view_actions details').removeAttribute('open');
            } else {
                setAlert('error', 'Leider ist etwas schief gelaufen. Bitte versuchen Sie es zu einem späteren Zeitpunkt erneut.');
            }
        }
    }, false);
}

